import type { AiSearchResponse } from '@/shared/api/graphql/aiSearch';
import { AI_SEARCH, AiSearchStep } from '@/shared/api/graphql/aiSearch';
import { useTranslation } from '@/shared/lib/common/i18n';
import { useRecentQuestionsStore } from '@/shared/model/RecentQuestionsStore';
import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import type { AskPatient } from './aiSearchStore';
import { useAiSearchStore } from './aiSearchStore';

/** The patient whose page is open (/patients/:truentityId/...), so "this patient" means them. */
export const openPatientId = (pathname: string) =>
  matchPath({ path: '/patients/:truentityId', end: false }, pathname)?.params.truentityId ?? null;

/**
 * Starts a search as a new turn in the panel, opening it. One HTTP request returns the answer.
 * `patient` asks about that patient instead of the open page's (one picked from a list).
 */
export const useAskAiSearch = () => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const truentityId = openPatientId(useLocation().pathname);

  return useCallback(
    (rawQuestion: string, patient?: AskPatient) => {
      const question = rawQuestion.trim();
      if (!question) return;

      const { addTurn, attachSearchId, failTurn, applyEvent } = useAiSearchStore.getState();
      const turnId = uuidv4();
      const searchId = uuidv4();
      addTurn({ id: turnId, question, patient });
      attachSearchId(turnId, searchId);
      // "...this patient" means nothing once the picked patient is gone, so it isn't kept.
      if (!patient) useRecentQuestionsStore.getState().recordQuestion(question);

      client
        .query<AiSearchResponse>({
          query: AI_SEARCH,
          variables: { question, truentityId: patient?.truentityId ?? truentityId },
          fetchPolicy: 'no-cache',
          errorPolicy: 'all'
        })
        .then(({ data, errors }) => {
          const result = data?.aiSearch;
          // errorPolicy is 'all', so a rejected question resolves with errors instead of throwing.
          if (!result) return failTurn(turnId, errors?.[0]?.message ?? t.aiSearch.errors.unavailable);

          // Recorded as the search's final step, which the panel reads the answer from.
          applyEvent({
            searchId,
            seq: 1,
            step: AiSearchStep.COMPLETED,
            status: 'DONE',
            message: null,
            detail: null,
            result,
            error: null,
            suggestions: result.suggestions
          });
        })
        .catch(() => failTurn(turnId, t.aiSearch.errors.unavailable));
    },
    [client, t, truentityId]
  );
};
